// src/StripeSubscribeButton.jsx
import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import LogoLink from './LogoLink.jsx';
import './StripeSubscribe.css';

const STRIPE_CHECKOUT_ORIGIN = 'https://checkout.stripe.com';

// Only ever leave the site for Stripe Checkout, whatever the API returns.
export const isStripeCheckoutUrl = (url) => {
  try {
    return new URL(url).origin === STRIPE_CHECKOUT_ORIGIN;
  } catch {
    return false;
  }
};

const StripeSubscribeButton = () => {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {

    if (auth.isLoading) {
      setError('Still loading authentication... please wait a moment and try again.');
      return;
    }

    const cognitoUsername = auth.user?.profile?.sub;
    const email = auth.user?.profile?.email;


    if (!auth.isAuthenticated || !cognitoUsername || !email) {
      setError('Not logged in. Redirecting to sign in page...');
      setTimeout(() => {
        window.location.href = '/signup';
      }, 1500);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const idToken = auth.user?.id_token;
      
      if (!idToken) {
        setError('Authentication error. Please sign in again.');
        auth.signinRedirect();
        return;
      }

      const response = await fetch(
        'https://ye24yvl82a.execute-api.us-east-2.amazonaws.com/prod/CreateStripeCheckoutSession',
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ cognitoUsername, email }),
        }
      );

      const data = await response.json();

      if (response.ok && isStripeCheckoutUrl(data.checkoutUrl)) {
        window.location.href = data.checkoutUrl;
      } else {
        console.error('Checkout session request failed:', response.status);
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout session request error:', err.message);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="subscribe-page-container">
      <div className="subscribe-card">
        <LogoLink className="logo-link--card" />
        {/* 🎉 FREE TRIAL BADGE */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '25px',
          display: 'inline-block',
          fontWeight: '700',
          marginBottom: '20px',
          fontSize: '0.95rem',
          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
        }}>
          🎉 30-DAY FREE TRIAL
        </div>

        <h1>Try TickrTrends Free</h1>
        
        <p style={{ fontSize: '1.05rem', color: '#8a94a2', marginBottom: '25px' }}>
          Start your free trial today. No charge for 30 days.
        </p>
        
        <p>Get unlimited access to:</p>
        
        <ul>
          <li>S&P 500 Forward Fair Value Analysis</li>
          <li>Weekly Rising Stars Report</li>
          <li>Real-time stock momentum tracking</li>
          <li>AI-powered market insights</li>
          <li>Proprietary valuation metrics</li>
        </ul>
        
        <div className="pricing-box">
          <div className="price-amount" style={{ fontSize: '3rem' }}>
            FREE
            <span className="price-period">for 30 days</span>
          </div>
          <p className="price-note">Then $4.99/month • Cancel anytime</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          onClick={handleSubscribe}
          disabled={loading || auth.isLoading}
          className="subscribe-button"
        >
          {auth.isLoading ? 'Loading...' : loading ? 'Processing...' : 'Start Free Trial'}
        </button>

        <p className="security-badge">
          🔒 Secure payment powered by Stripe • Card required but not charged for 30 days
        </p>
      </div>
    </div>
  );
};

export default StripeSubscribeButton;